import { useState } from "react";

/**
 * The last value a Convex query returned, held across an ARGUMENT change.
 *
 * `useQuery` returns `undefined` while it waits for the server, and it starts
 * waiting again whenever its args change — which for anything driven by a clock
 * is on every tick, however coarse. Without this, a stats panel drops back to
 * "Counting…" every five minutes, taking an expanded table and any hover state
 * with it, and the paywall's price block unmounts for a round trip once a
 * minute, shifting the layout under the reader's cursor (review, sales branch).
 *
 * Holding the previous value is the right trade for these surfaces: it is a
 * count from five minutes ago rather than a blank, and the value that replaces
 * it is at most one round trip behind. It would be the WRONG trade for anything
 * whose args name a different SUBJECT — showing chapter 3's numbers under
 * chapter 4's heading — so callers remount those on the subject (a React `key`)
 * rather than reaching for this.
 *
 * STATE ADJUSTED DURING RENDER, not a ref and not an effect. A ref written in
 * the render body is what this obviously wants and is exactly what
 * `react-hooks/refs` forbids (a ref write cannot schedule the render that would
 * show it); an effect is `react-hooks/set-state-in-effect` and would show one
 * blank frame before correcting. What is left is React's documented
 * "adjust state when a prop changes" pattern — a setState in the render body,
 * which React re-runs immediately without committing the discarded output. The
 * identity check is what makes it terminate: `useQuery` returns a stable
 * reference while its result is unchanged.
 */
export function useLastLoaded<T>(value: T | undefined): T | undefined {
  const [last, setLast] = useState<T | undefined>(undefined);
  if (value !== undefined && value !== last) setLast(value);
  return value ?? last;
}
